// 1枚目（正面画像）アップロード後のカテゴリ判定処理。
//
// direct_upload.js から upload-success フックで呼ばれる。
// 判定APIは PIX バックエンド経由（/pix/api/v1/category-classifications）で外部APIを叩く。
import axios from 'axios';
import { openCategoryCandidatesModal } from './category_candidates_modal';

const ENDPOINT = '/pix/api/v1/category-classifications';

// high 自動セット後にユーザがカテゴリ変更した時に表示するアラート文言。
// 文言調整時にファイル内で grep 一回で済むよう定数化する。
const CATEGORY_CHANGE_ALERT_MESSAGE = '画像から自動判定したカテゴリです。変更する際はご注意ください。';

// 現在表示中の 1 枚目画像の imageId（= Shrine cache key）。
// 削除→再アップロード時に古い画像の判定 API 応答が遅延到着したケースで、識別子を比較して破棄する。
// medium 経路はカテゴリ変更アラートの発火条件外で silent 誤セットが起きうるため世代管理で保護する。
let currentPrimaryImageId = null;

// 1枚目アップロード完了時のフック。
// file: Uppy file object (file.meta.key = "cache/<id>"、file.preview = サムネ data URL)
export async function handlePrimaryUploadSuccess(file) {
  // カテゴリ判定は適用先のカテゴリ select を持つフォーム専用。本ハンドラを共有する LP(lp2/lp3/lp11) 等の
  // カテゴリ固定フォームでは結果を適用できず、外部AI課金だけが無駄に発生するため呼ばない。
  if (!hasEditableCategorySelect()) return;

  const imageId = extractImageId(file);
  if (!imageId) return;
  currentPrimaryImageId = imageId;

  const prediction = await classifyImage(imageId);
  // 応答を待っている間に別画像に差し替わっていたら、古い判定結果は破棄する（race condition 対策）
  if (currentPrimaryImageId !== imageId) return;
  if (!prediction) return; // API失敗時は手動選択にフォールバック（low相当）

  applyPrediction(prediction, file);
}

// 1 枚目画像が削除された時に呼ぶ。判定 API 応答がまだ in-flight でも、到着後に差し替わり検知で破棄されるよう
// 現在の imageId 参照を切る。direct_upload.js の削除ハンドラから呼び出す想定。
export function resetPrimaryImageTracking() {
  currentPrimaryImageId = null;
}

// 判定結果を適用できる編集可能なカテゴリ select があるか。
// LP は同 id を hidden input で描画するため、id の存在だけでなくタグ種別まで見る。
function hasEditableCategorySelect() {
  return document.getElementById('item_parent_category_id') instanceof HTMLSelectElement;
}

function extractImageId(file) {
  // file.meta.key は "cache/<id>" 形式（Shrine の cache prefix を反映）
  const match = file?.meta?.key?.match(/^cache\/(.+)/);
  return match?.[1] || null;
}

async function classifyImage(imageId) {
  // 判定APIは認証無し・CSRF 必須。csrf_meta_tags の token を X-CSRF-Token で送る。
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (!csrfToken) {
    // token 不在は必ず 403。無駄打ちを避け、API障害と区別できるよう警告して手動選択にフォールバック。
    console.warn('カテゴリ判定API: csrf-token meta が見つからないため判定をスキップします');
    return null;
  }

  try {
    const res = await axios.post(ENDPOINT, { image_id: imageId }, {
      headers: { 'X-CSRF-Token': csrfToken }
    });
    return res.data?.prediction || null;
  } catch (e) {
    // ネットワーク・5xx等は手動選択にフォールバック。Rollbar 等の通知は不要（外部API障害は別経路で気づく）
    console.warn('カテゴリ判定APIの呼び出しに失敗しました', e);
    return null;
  }
}

function applyPrediction(prediction, file) {
  // 再判定のたびに high フラグを初期化し、前回の high 判定が medium/low に持ち越されてアラートが誤発火するのを防ぐ
  highAutoSetActive = false;
  switch (prediction.confidence) {
    case 'high':
      // 自動セットが成功した場合のみ guard を仕掛ける。option 不在等で silent に失敗したケースで
      // 何もセットされていないのに「自動判定したカテゴリです」アラートが出る嘘警告を防ぐ。
      if (autoSelectCategory(prediction)) {
        // high 自動セット直後のみカテゴリ変更アラート対象
        highAutoSetActive = true;
        installCategoryChangeGuard();
      }
      break;
    case 'medium':
      showCandidatesModal(prediction, file);
      break;
    case 'low':
    default:
      // 手動選択のまま（何もしない）
      break;
  }
}

// medium 時に候補モーダルを開く。
// ユーザーが候補を選んで「選択する」を押したら autoSelectCategory と同じ経路（親/子リーフ自動セット）を通す。
// 「選択せず次へ」/ キャンセル時は何もしない（low と同じ = 手動選択）。
// バックエンド解決が全滅（candidates が空）した場合もモーダルを出さず手動選択へフォールバック。
function showCandidatesModal(prediction, file) {
  const candidates = prediction.candidates || [];
  if (candidates.length === 0) return;

  openCategoryCandidatesModal(candidates, {
    imageUrl: file?.preview,
    onSelect: (candidate) => {
      if (!candidate) return;
      autoSelectCategory(candidate);
    }
  });
}

// 直近の自動セットが high 由来で「カテゴリ変更アラート対象」状態かを保持する。
// medium モーダル経由・low / 手動選択は対象外。一度アラートを出したら false に戻し、再表示しない。
let highAutoSetActive = false;

// 自動セット / リセット時の dispatchEvent('change') を「ユーザー操作」と誤認しないためのフラグ。
// selModal 経由のユーザー操作は isTrusted=false になり得るため、イベントの isTrusted ではなくフラグ方式で判定する。
let isSettingProgrammatically = false;

// select.value 変更と change 発火をプログラマティック操作としてマークしつつ実行するヘルパー。
function setSelectProgrammatically(select, value) {
  isSettingProgrammatically = true;
  try {
    select.value = value;
    select.dispatchEvent(new Event('change'));
  } finally {
    isSettingProgrammatically = false;
  }
}

// カテゴリ select の変更監視を 1 度だけ bind する。highAutoSetActive なユーザー操作で alert を出し、フラグをクリアする。
// 変更自体はブロックしない（仕様: アラート表示のみ）。
let categoryChangeGuardInstalled = false;
function installCategoryChangeGuard() {
  if (categoryChangeGuardInstalled) return;
  const parentSelect = document.getElementById('item_parent_category_id');
  const childSelect = document.getElementById('item_child_category_id');
  if (!parentSelect && !childSelect) return;
  categoryChangeGuardInstalled = true;

  const handler = () => {
    if (isSettingProgrammatically) return;
    if (!highAutoSetActive) return;
    // ブロックせず通知のみ（「変更自体はブロックしない」）
    alert(CATEGORY_CHANGE_ALERT_MESSAGE);
    highAutoSetActive = false;
  };
  parentSelect?.addEventListener('change', handler);
  childSelect?.addEventListener('change', handler);
}

// AI返却のリーフカテゴリを select にセットする。
// - 親リーフ（parent_category_id=null）: 親 select に直接セット
// - 子リーフ（parent_category_id=非null）: 親 select に親IDをセットして Vue の loadChildCategories を発火させ、
//   子 select に leaf option が出現したら value をセット
// @return {boolean} 自動セットを実際に行った場合 true / option 不在等で何もできなかった場合 false
function autoSelectCategory(prediction) {
  if (prediction.parent_category_id) {
    return setChildLeafCategory(prediction.parent_category_id, prediction.category_id);
  } else {
    return setParentCategory(prediction.category_id);
  }
}

// 親カテゴリ select に値をセットし、Vue の @change ハンドラと selmodal の表示を同期する。
// @return {boolean} set 成功時 true / select や option が見つからない場合 false
function setParentCategory(categoryId) {
  const select = document.getElementById('item_parent_category_id');
  if (!select) return false;

  // option が存在しないカテゴリIDが返ってきた場合は上書きしない（API/DBの不整合に備える）
  const option = select.querySelector(`option[value="${CSS.escape(categoryId)}"]`);
  if (!option) {
    console.warn(`カテゴリ自動セット失敗: option not found for category_id=${categoryId}`);
    return false;
  }

  // Vue の @change ハンドラ（loadChildCategories / toggleItemImageRequired 等）を発火。
  // プログラマティック set としてマークし、カテゴリ変更アラートが誤発火しないようにする。
  setSelectProgrammatically(select, categoryId);
  syncSelmodal();
  return true;
}

// 子リーフを自動セットする。親 select に parentId をセット → Vue の loadChildCategories（axios async）
// 完了を MutationObserver で待ち、子 select の option が揃ったら leafId をセットする。
// items.js 側のVueメソッドに手を入れず、DOM変化を監視する形で疎結合に保つ。
// @return {boolean} 親 select への set が成功し observer を仕掛けた場合 true /
//   select/option 不在で何もできなかった場合 false。子 select の set は非同期で、本関数の戻り値には反映しない
const CHILD_LEAF_OBSERVER_TIMEOUT_MS = 5000;

// 本関数は連続呼び出し（例: medium→選択→削除→再アップロード→high で別 leaf）で
// 前回の observer が残ったまま新規 observer を作ると、前回の leafId が偶然 DOM 上に現れた瞬間に
// 古い leaf を set してしまう副作用がある。モジュールレベルに現行 observer と timeout を保持して、
// 新規 set の冒頭で必ず片付けるようにする。
let currentChildObserver = null;
let currentChildObserverTimeoutId = null;
function cleanupCurrentChildObserver() {
  if (currentChildObserver) {
    currentChildObserver.disconnect();
    currentChildObserver = null;
  }
  if (currentChildObserverTimeoutId) {
    clearTimeout(currentChildObserverTimeoutId);
    currentChildObserverTimeoutId = null;
  }
}

function setChildLeafCategory(parentId, leafId) {
  const parentSelect = document.getElementById('item_parent_category_id');
  const childSelect = document.getElementById('item_child_category_id');
  if (!parentSelect || !childSelect) return false;

  const parentOption = parentSelect.querySelector(`option[value="${CSS.escape(parentId)}"]`);
  if (!parentOption) {
    console.warn(`カテゴリ自動セット失敗: parent option not found for parent_category_id=${parentId}`);
    return false;
  }

  // 前回の自動セット用 observer/timeout を破棄してから新規セットアップする
  cleanupCurrentChildObserver();

  setSelectProgrammatically(parentSelect, parentId);
  syncSelmodal();

  // MutationObserver.disconnect() は「その後キューされる通知」を止めるが、すでに
  // マイクロタスクキューに積まれた callback は走る（MDN 仕様）。連続呼び出しで古い observer の
  // 遅延 callback が新 observer を誤って disconnect してしまうのを防ぐため、closure で自身の
  // 参照を保持し、現行 observer と一致しない場合は早期 return する。
  const observer = new MutationObserver(() => {
    if (currentChildObserver !== observer) return;

    const leafOption = childSelect.querySelector(`option[value="${CSS.escape(leafId)}"]`);
    if (!leafOption) return;

    setSelectProgrammatically(childSelect, leafId);
    syncSelmodal();
    cleanupCurrentChildObserver();
  });
  currentChildObserver = observer;
  // items.js の v-for は :key 未指定で Vue が in-place patch するため、option 要素の
  // 追加/削除が起きないケースがある（前親と新親の子カテゴリ数が同じ等）。その場合
  // childList のみを観測すると発火せず子リーフが永久にセットされないため、
  // option の value 属性変更・text ノード変更（in-place patch 経路）も検知する。
  observer.observe(childSelect, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['value'],
    characterData: true
  });

  // ネット遅延・API失敗等で子 option が永遠に来ないケースの保険。子セットは諦めて手動選択にフォールバック。
  currentChildObserverTimeoutId = setTimeout(cleanupCurrentChildObserver, CHILD_LEAF_OBSERVER_TIMEOUT_MS);
  return true;
}

// selmodal（カスタム select UI）の表示を新しい選択値に同期
function syncSelmodal() {
  if (window.jQuery && window.jQuery.fn.selModal) {
    window.jQuery('.selmodal').selModal();
  }
}
